/**
 * A <select multiple> control
 *
 * Available Options:
 *     - name (required) - string
 *     - options (optional)
 *         [ { name: 'display value', value: [Number] } ]
 *     - optGroups (optional)
 *         [ { label: 'display value' || null, options: <options struct> } ]
 *     - value (optional) - [Number]
 *     - placeholder (optional) - string
 *     - mobile (optional) - true/false
 */
import $ from 'jquery'
import SearchFormControl from 'component/search_form/control/control'
import template from 'component/search_form/control/pick/pick.tmpl'
import mobileTemplate from 'component/search_form/control/pick/pick_mobile.tmpl'

export default SearchFormControl.extend({
    templates: {
        default: template,
        mobile: mobileTemplate,
    },

    defaultOptions: {
        mobile: false,
        multiple: true,
        name: null,
        options: null,
        optGroups: null,
        placeholder: null,
        value: null,
    },

    initialize(options) {
        if (!options || (!options.options && !options.optGroups)) {
            throw new Error('Missing required option: options or optGroups')
        }

        this.options = this.pickOptions(options, this.defaultOptions)
        this.listenTo(this, 'change', this._onChange)

        return SearchFormControl.prototype.initialize.apply(this, arguments) // eslint-disable-line prefer-rest-params
    },

    events: {
        'change input[type=checkbox]': '_onCheckboxInputChange',
        'change select': '_onSelectChange',
    },

    render() {
        const template = this.templates[this.options.mobile ? 'mobile' : 'default']

        this.$el.html(
            template({
                name: this._name,
                optGroups: this._getOptGroupsWithState(),
                multiple: this.options.multiple,
            })
        )

        return this
    },

    _getOptGroupsWithState() {
        const value = this._value

        const optGroups = this.options.optGroups || [{ label: null, options: this.options.options }]

        const getOptions = (options, spaceBottom) =>
            options.map((option, i) =>
                Object.assign({}, option, {
                    id: `${this._name}-option-${option.value}`,
                    selected: value && value.indexOf(option.value) !== -1,
                    spaceBottom: spaceBottom && i === options.length - 1,
                })
            )

        const withState = optGroups.map((optGroup, i) => {
            const spaceBottom = optGroups.length > 1 && i < optGroups.length - 1
            return Object.assign({}, optGroup, {
                options: getOptions(optGroup.options, spaceBottom),
            })
        })

        if (this.options.mobile && this.options.placeholder) {
            if (withState.length > 1) {
                withState.unshift({ label: null, options: [] })
            }

            withState[0].options.unshift({
                value: '',
                name: this.options.placeholder,
                selected: !value || !value.length,
            })
        }

        return withState
    },

    _onCheckboxInputChange(e) {
        const checked = $(e.currentTarget).is(':checked')
        const value = $(e.currentTarget).val()

        this._updateValue(value, checked)
    },

    _onSelectChange(e) {
        this._setValue($(e.currentTarget).val())
    },

    _onChange() {
        if (!this.options || !this.options.mobile) {
            this.render()
        }
    },

    _updateValue(value, toggle) {
        if (this.options.multiple) {
            return this._toggleValue(value, toggle)
        }

        return this._setValue(toggle ? [value] : [])
    },

    _toggleValue(value, toggle) {
        let newValue

        // Don't modify original, set via this.value();
        if (toggle) {
            newValue = (this._value || []).concat([value])
        } else {
            newValue = this._value && this._value.filter((existingValue) => existingValue !== parseInt(value, 10))
        }

        this._setValue(newValue)
    },

    _setValue(val) {
        if (!Array.isArray(val)) {
            val = [val]
        }

        // Remove empty items (i.e. the user selected placeholder) and make all numbers
        val = val.filter((item) => item).map((item) => parseInt(item, 10))

        // There was no value selected
        if (val.length === 0) {
            val = null
        }

        this.value(val)
    },
})
